import { analyticsApi, projectApi } from '@shared/api-client';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { BreakdownPanel } from '@/components/analytics/breakdown-panel';
import { LivePulse } from '@/components/analytics/live-pulse';
import { RealtimeLineChart } from '@/components/analytics/realtime-line-chart';
import { Alert } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCompactNumber, formatRelative } from '@/lib/utils';

const REFRESH_INTERVAL_MS = 10_000;

const Metric = ({
  label,
  value,
  detail,
  live = false,
}: {
  label: string;
  value: number;
  detail: string;
  live?: boolean;
}) => (
  <div className="min-w-0 px-5 py-4 sm:px-6">
    <div className="flex items-center gap-2 text-xs font-medium text-ink-muted">
      {live ? <LivePulse active={value > 0} /> : null}
      <span>{label}</span>
    </div>
    <p className="mt-2 text-2xl font-semibold tracking-tight text-ink tabular-nums">
      {formatCompactNumber(value)}
    </p>
    <p className="mt-1 text-xs text-ink-subtle">{detail}</p>
  </div>
);

export const RealtimePage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: projectApi.listProjects,
  });
  const realtimeQuery = useQuery({
    queryKey: ['analytics', 'project', projectId, 'realtime'],
    queryFn: () => analyticsApi.getRealtime(projectId!),
    enabled: Boolean(projectId),
    staleTime: 0,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
  const project = projectsQuery.data?.projects.find(
    (candidate) => candidate.id === projectId,
  );
  const realtime = realtimeQuery.data;

  if (projectsQuery.isLoading) {
    return <Skeleton className="h-[560px] rounded-card" />;
  }

  if (!project) {
    return (
      <Card className="p-8 text-center shadow-none">
        <h1 className="text-lg font-semibold text-ink">Project not found</h1>
        <p className="mt-2 text-sm text-ink-muted">
          It may have been deleted or belong to another organization.
        </p>
        <Link
          to="/"
          className="mt-5 inline-flex text-sm font-medium text-accent hover:text-accent-hover"
        >
          Back to projects
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-ink">
            Realtime
          </h1>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-muted">
            <span>{project.primaryDomain ?? 'No primary domain'}</span>
            {realtime?.summary.lastEventAt ? (
              <span>
                Last event {formatRelative(realtime.summary.lastEventAt)}
              </span>
            ) : null}
          </div>
        </div>

        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-positive-subtle px-3 py-1.5 text-xs font-medium text-ink">
          <LivePulse active={realtimeQuery.isSuccess} />
          {realtimeQuery.isError
            ? 'Reconnecting · retrying automatically'
            : realtimeQuery.isSuccess
              ? 'Live · refreshes every 10 seconds'
              : 'Connecting to live data'}
        </div>
      </div>

      {realtimeQuery.isError ? (
        <Alert tone="error">
          Realtime analytics could not be loaded. Collection continues
          independently; the dashboard will retry automatically.
        </Alert>
      ) : null}

      {realtimeQuery.isLoading || !realtime ? (
        <div className="space-y-4">
          <Skeleton className="h-28 rounded-card" />
          <Skeleton className="h-[360px] rounded-card" />
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-72 rounded-card" />
            <Skeleton className="h-72 rounded-card" />
          </div>
        </div>
      ) : (
        <>
          <Card className="grid overflow-hidden shadow-none sm:grid-cols-2 lg:grid-cols-4 [&>*:not(:first-child)]:border-t sm:[&>*]:border-t-0 sm:[&>*:nth-child(even)]:border-l sm:[&>*:nth-child(n+3)]:border-t lg:[&>*:nth-child(n+3)]:border-t-0 lg:[&>*:nth-child(n+2)]:border-l">
            <Metric
              label="Active now"
              value={realtime.summary.currentVisitors}
              detail={`Meaningful activity in the last ${realtime.activeWindowMinutes} min`}
              live
            />
            <Metric
              label="Unique visitors"
              value={realtime.summary.visitors30m}
              detail="Last 30 minutes"
            />
            <Metric
              label="Pageviews"
              value={realtime.summary.pageviews30m}
              detail="Last 30 minutes"
            />
            <Metric
              label="Events"
              value={realtime.summary.events30m}
              detail="Last 30 minutes"
            />
          </Card>

          <Card className="overflow-hidden p-4 shadow-none sm:p-5">
            <div className="mb-2 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
              <div>
                <h2 className="text-sm font-semibold text-ink">
                  Active visitors
                </h2>
                <p className="mt-0.5 text-xs text-ink-muted">
                  Minute-by-minute activity over the last 30 minutes
                </p>
              </div>
              <span className="text-xs text-ink-subtle">
                Updated {formatRelative(realtime.queriedAt)}
              </span>
            </div>
            <RealtimeLineChart points={realtime.timeseries} />
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <BreakdownPanel
              tabs={[
                {
                  id: 'channel',
                  label: 'Channels',
                  itemLabel: 'Channel',
                  items: realtime.breakdowns.channel,
                  value: 'visitors',
                },
                {
                  id: 'source',
                  label: 'Sources',
                  itemLabel: 'Source',
                  items: realtime.breakdowns.source,
                  value: 'visitors',
                },
                {
                  id: 'campaign',
                  label: 'Campaigns',
                  itemLabel: 'Campaign',
                  items: realtime.breakdowns.campaign,
                  value: 'visitors',
                },
              ]}
            />
            <BreakdownPanel
              tabs={[
                {
                  id: 'page',
                  label: 'Current pages',
                  itemLabel: 'Page',
                  items: realtime.breakdowns.page,
                  value: 'visitors',
                },
                {
                  id: 'event',
                  label: 'Product events',
                  itemLabel: 'Event · last 30 min',
                  items: realtime.breakdowns.event,
                  value: 'events',
                },
              ]}
            />
            <BreakdownPanel
              tabs={[
                {
                  id: 'country',
                  label: 'Countries',
                  itemLabel: 'Country',
                  items: realtime.breakdowns.country,
                  value: 'visitors',
                },
              ]}
            />
            <BreakdownPanel
              tabs={[
                {
                  id: 'browser',
                  label: 'Browsers',
                  itemLabel: 'Browser',
                  items: realtime.breakdowns.browser,
                  value: 'visitors',
                },
                {
                  id: 'os',
                  label: 'Operating systems',
                  itemLabel: 'Operating system',
                  items: realtime.breakdowns.os,
                  value: 'visitors',
                },
                {
                  id: 'device',
                  label: 'Devices',
                  itemLabel: 'Device',
                  items: realtime.breakdowns.device,
                  value: 'visitors',
                },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
};
